import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiTarget,
  FiEye,
  FiHeart,
  FiUsers,
  FiGlobe,
  FiAward,
} from "react-icons/fi";
import axios from "axios";

function About() {
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(
        "https://agri-back-mauve.vercel.app/api/content/public/about"
      );
      if (response.data.success && response.data.data.length > 0) {
        setAboutContent(response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching about content:", error);
    } finally {
      setLoading(false);
    }
  };

  const values = [
    {
      icon: <FiTarget className="w-8 h-8" />,
      title: "Our Mission",
      description:
        "To enhance food and nutrition security and income of farmers by improving delivery of agricultural technologies, products, and services.",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: <FiEye className="w-8 h-8" />,
      title: "Our Vision",
      description:
        "To contribute to agricultural development through use of innovative technologies, products, and services.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FiHeart className="w-8 h-8" />,
      title: "Our Values",
      description:
        "Innovation, sustainability, and empowerment drive everything we do for the agricultural community.",
      gradient: "from-orange-500 to-red-500",
    },
  ];

  const stats = [
    {
      number: "250+",
      label: "Farms Transformed",
      icon: <FiUsers className="w-6 h-6" />,
    },
    { number: "15", label: "Countries", icon: <FiGlobe className="w-6 h-6" /> },
    {
      number: "40%",
      label: "Average Yield Increase",
      icon: <FiAward className="w-6 h-6" />,
    },
    {
      number: "98%",
      label: "Client Satisfaction",
      icon: <FiHeart className="w-6 h-6" />,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
          >
            About{" "}
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              Afrikonsults
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {aboutContent?.description ||
              "Leading the digital transformation of African agriculture through innovative technology solutions."}
          </motion.p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl font-bold mb-2">{stat.number}</div>
                <div className="text-green-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Afrikonsults
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r ${value.gradient} rounded-2xl flex items-center justify-center text-white`}
                >
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="prose prose-lg mx-auto"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-gray-600 mb-6">
                Founded with a vision to revolutionize African agriculture,
                Afrikonsults brings together cutting-edge technology and deep
                agricultural expertise to solve the most pressing challenges
                facing farmers across the continent.
              </p>
              <p className="text-gray-600 mb-6">
                Our team of agronomists, data scientists, and technology experts
                work hand-in-hand with farmers to develop solutions that are not
                only innovative but also practical and sustainable for the
                African context.
              </p>
              <p className="text-gray-600">
                Today, we're proud to serve hundreds of farms across all African
                countries, helping them increase yields, reduce costs, and build
                more sustainable agricultural practices for future generations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
